import * as React from "react";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import {
    trackEvent,
    trackViewedModal,
    captureException,
} from "~/lib/analytics";
import { logger } from "~/lib/logger";
import { cn } from "~/lib/utils";

type TrackedDialogProps = DialogPrimitive.DialogProps & {
    /** Optionally name this dialog for analytics (e.g., "LoginModal") */
    analyticsId: string;
};

const Dialog = ({
    analyticsId = "UnnamedDialog",
    onOpenChange,
    ...props
}: TrackedDialogProps) => {
    // Wrap the Radix <DialogPrimitive.Root> and invoke trackViewedModal when opening
    function handleOpenChange(open: boolean) {
        try {
            if (open) {
                trackEvent("viewed modal", { modal: analyticsId });
            }
            onOpenChange?.(open);
        } catch (err) {
            captureException("Error in dialog component", err);
        }
    }

    return <DialogPrimitive.Root onOpenChange={handleOpenChange} {...props} />;
};

const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        className={cn(
            "fixed inset-0 z-50 bg-black/20  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            className,
        )}
        {...props}
    />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

// AIDEV-NOTE: Uniform rounded-3xl corners (design refresh Sep 2026 — formerly mixed
// rounded-md with tl/br 3xl; the higher value won on all four).
const DialogContent = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
        showCloseButton?: boolean;
    }
>(({ className, children, showCloseButton = true, ...props }, ref) => (
    <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
            ref={ref}
            className={cn(
                "fixed left-[50%] top-[50%] z-50 grid w-[calc(100vw-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-brand-terra-100 bg-background p-4 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-3xl sm:w-5/6 sm:p-6",
                "max-h-[90vh] overflow-y-auto",
                className,
            )}
            {...props}
        >
            {children}
            {showCloseButton && (
                <DialogPrimitive.Close className="absolute right-4 top-4 z-10 rounded-sm text-brand-forest-700 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                    <X className="size-5" strokeWidth={3} />
                    <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
            )}
        </DialogPrimitive.Content>
    </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

// AIDEV-NOTE: WHITE MASTHEAD (design refresh Sep 2026) — the header bleeds to the
// dialog edges via negative margins (mirroring DialogContent's p-4/sm:p-6, so a
// dialog that overrides that padding must restyle its header too) and carries the
// title + the absolutely-positioned close X on a white band above the cream body.
// `badge` is an optional node (e.g. <Badge variant="terra">NEW</Badge>) rendered
// above the title; existing dialogs pass nothing and render exactly as before,
// minus none of their content. pr-8 keeps the title clear of the X.
const DialogHeader = ({
    className,
    badge = null,
    children,
    ...props
}: React.HTMLAttributes<HTMLDivElement> & { badge?: React.ReactNode }) => (
    <div
        className={cn(
            "-mx-4 -mt-4 flex flex-col space-y-1.5 rounded-t-3xl border-b border-brand-forest-100 bg-white px-4 pb-4 pt-5 pr-10 text-center sm:-mx-6 sm:-mt-6 sm:px-6 sm:pr-10 sm:text-left",
            className,
        )}
        {...props}
    >
        {badge ? <div className="leading-none">{badge}</div> : null}
        {children}
    </div>
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
            className,
        )}
        {...props}
    />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        // AIDEV-NOTE: Brand serif (Playfair via font-display), one step larger than the
        // old text-lg sans — part of the masthead design refresh (Sep 2026).
        className={cn(
            "font-display text-xl font-semibold leading-snug tracking-tight text-brand-forest-800",
            className,
        )}
        {...props}
    />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Description
        ref={ref}
        className={cn("text-md pt-2 text-brand-forest-800", className)}
        {...props}
    />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogClose,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
};
